import * as Dialog from "@radix-ui/react-dialog";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils/cn";
import { restoreFocus, useFocusRestoreTarget } from "@/lib/a11y/focusRestore";

export interface ActionSheetAction {
  /** Stable id used in tests + callback. */
  id: string;
  /** Visible label (繁中 per project convention). */
  label: string;
  /** Optional leading icon. */
  leadingIcon?: ReactNode;
  /**
   * Visual tone. `danger` renders in `partner-strong` (pink/red); `default`
   * uses ink-1. `cancel` is reserved for the built-in cancel row (do NOT pass).
   */
  tone?: "default" | "danger";
  /** Disable the action. */
  disabled?: boolean;
  testId?: string;
  /** Click handler. */
  onClick: () => void;
}

export interface ActionSheetProps {
  /** Controls sheet visibility. */
  open: boolean;
  /** Close handler (cancel button + Esc + backdrop + drag-dismiss). */
  onClose: () => void;
  /** Title shown at top of sheet (e.g. "三週年那天要寫什麼"). */
  title: string;
  /**
   * Action buttons rendered top-down (danger-first per iOS HIG action sheet convention).
   * If `tone: 'danger'` is set, the row renders in `text-partner-strong`.
   */
  actions: readonly ActionSheetAction[];
  /**
   * Cancel button label. Default: "取消". Pass `null` to hide the cancel row
   * (caller is responsible for closing via Esc / backdrop).
   */
  cancelLabel?: string | null;
  /**
   * Show drag-to-dismiss handle at top. Per `mobile.html §5.1`, threshold is
   * 100 px and a `vibrate(6)` fires on dismiss.  Default: true.
   */
  dragToDismiss?: boolean;
  /**
   * Drag-dismiss threshold in px. Default: 100 (per `mockup/MOBILE.md §5.1`).
   */
  dragThresholdPx?: number;
  /** Optional testId for E2E / screenshot automation. */
  testId?: string;
}

/**
 * W56.0 E — what counts as "the user pressed a control" for the drag guard.
 *
 * A press that lands on a `<button>` is a **tap on that button**, never the
 * start of a drag: the row's own `onClick` is what the user meant, and
 * swallowing it into a 100px drag is the 「点一次没反应」 family of defect.
 * `BottomSheet` has carried this predicate since W48.0 (ADR-0144); the
 * ActionSheet's drag grew without it, so the same guard is applied here.
 */
const INTERACTIVE =
  "button, a[href], input, select, textarea, label, [role='button'], [contenteditable='true']";

/**
 * ActionSheet — iOS-style bottom action sheet.
 *
 * Per `mockup/mobile.html §6 + §8.2`:
 *   - Triggered by long-press 250 ms (use `useLongPress` hook from
 *     `@/lib/gestures/longPress`).
 *   - Centered action list (NO header) + 取消 cancel row below.
 *   - Drag handle at top; drag > `dragThresholdPx` (default 100) closes with
 *     `vibrate(6)` haptic.
 *   - Backdrop tap / Esc also closes.
 *
 * Used by: diary-card long-press, anniversary-card long-press, wishlist-item
 * long-press, etc.
 *
 * **W56.0 E — built on Radix `Dialog`.** The sheet used to declare
 * `role="dialog" aria-modal="true"` by hand, which is a claim the DOM did not
 * back up: there was no focus trap, no focus restore, and no inert
 * background, so a keyboard user could Tab straight out of an open sheet into
 * the page behind it and, on close, land on `<body>`. Radix's modal dialog
 * supplies all three (focus trap + `hideOthers` inerting + `onCloseAutoFocus`),
 * and the repository already uses it everywhere else — `patterns/BottomSheet`
 * alone has 21 call sites. Two focus-management systems in one app is two
 * sets of rules fighting, so no `focus-trap` package was added.
 *
 * What the migration changed **structurally**:
 *   - the backdrop is `Dialog.Overlay` (it gets the dismissal behaviour),
 *   - the panel is `Dialog.Content` (it carries `role="dialog"`, the
 *     `aria-labelledby` wiring and the focus trap),
 *   - the title is `Dialog.Title` (an `<h2>`; Radix owns its id).
 *
 * What it deliberately did **not** change: the danger-first sort, the row
 * tones, the drag gesture and its 100px threshold, the `vibrate(6)` haptic,
 * and the two-cluster layout.
 */
export function ActionSheet({
  open,
  onClose,
  title,
  actions,
  cancelLabel = "取消",
  dragToDismiss = true,
  dragThresholdPx = 100,
  testId,
}: ActionSheetProps) {
  // W56.0 E — the element that had focus before the sheet opened. A controlled
  // `Dialog.Root` has no `Dialog.Trigger`, so Radix has nowhere to restore the
  // focus to and the sheet would leave it on `<body>`. The capture has to
  // happen during render (see the helper's header) and the restore happens in
  // `onCloseAutoFocus` below.
  const restoreTarget = useFocusRestoreTarget(open);
  const sheetRef = useRef<HTMLDivElement>(null);
  const [dragDelta, setDragDelta] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startYRef = useRef<number | null>(null);

  // Reset drag state on close.
  useEffect(() => {
    if (!open) {
      setDragDelta(0);
      setDragging(false);
      startYRef.current = null;
    }
  }, [open]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!dragToDismiss) return;
      // Only react to primary pointer.
      if (!e.isPrimary) return;
      // **W56.0 E** — a press that lands on a control is a tap on that control,
      // never the start of a drag. See `INTERACTIVE` above.
      const target = e.target as HTMLElement | null;
      if (target?.closest?.(INTERACTIVE)) return;
      startYRef.current = e.clientY;
      setDragging(true);

      const onMove = (ev: PointerEvent) => {
        if (startYRef.current == null) return;
        const delta = ev.clientY - startYRef.current;
        // Only allow downward drag (positive delta).
        setDragDelta(Math.max(0, delta));
      };

      const onUp = (ev: PointerEvent) => {
        const finalDelta = startYRef.current == null ? 0 : ev.clientY - startYRef.current;
        if (finalDelta > dragThresholdPx) {
          // Crossed threshold → dismiss + haptic.
          if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
            navigator.vibrate(6);
          }
          onClose();
        }
        setDragDelta(0);
        setDragging(false);
        startYRef.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancelEvt);
      };

      const onCancelEvt = () => {
        setDragDelta(0);
        setDragging(false);
        startYRef.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancelEvt);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancelEvt);
    },
    [dragToDismiss, dragThresholdPx, onClose],
  );

  if (!open) return null;

  // Sort: danger actions first (per iOS HIG), then default.
  const sorted = [...actions].sort((a, b) => {
    const aDanger = a.tone === "danger" ? 1 : 0;
    const bDanger = b.tone === "danger" ? 1 : 0;
    return bDanger - aDanger;
  });

  // Backdrop opacity follows drag progress (per §5.1 drag-to-dismiss)
  const backdropOpacity = Math.max(0, 1 - dragDelta / 240);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        {/*
          The wrapper keeps the sheet's outer geometry (`fixed inset-0 z-50`)
          and, more importantly, keeps `globals.css`'s drag rule alive:
          `[data-component="action-sheet"] [data-testid="action-sheet-panel"]`
          is a **descendant** selector, so the element carrying
          `data-component="action-sheet"` has to be an *ancestor* of the
          panel — not the panel itself.
        */}
        <div
          data-component="action-sheet"
          data-testid={testId ?? "action-sheet"}
          data-open="true"
          className="fixed inset-0 z-50"
        >
          <Dialog.Overlay
            data-testid="action-sheet-backdrop"
            className="absolute inset-0 bg-ink-1/40 backdrop-blur-sm animate-share-sheet-backdrop transition-opacity duration-quick"
            style={{ opacity: backdropOpacity }}
          />
          <Dialog.Content
            ref={sheetRef}
            data-testid="action-sheet-panel"
            data-dragging={dragging ? "true" : "false"}
            // Radix 1.1 renders `role="dialog"` on `Dialog.Content` but **not**
            // `aria-modal` — the attribute was dropped upstream — so the
            // modal claim is stated here explicitly.
            aria-modal="true"
            onPointerDown={handlePointerDown}
            // W56.0 E — give the focus back to whatever opened the sheet.
            onCloseAutoFocus={(event) => restoreFocus(event, restoreTarget.current)}
            className={cn(
              "absolute inset-x-0 bottom-0 max-h-[80vh]",
              "px-2 pb-[max(env(safe-area-inset-bottom),8px)]",
              "animate-share-sheet-panel",
              "transition-transform duration-base ease-quiet",
              "data-[dragging=true]:transition-none",
            )}
            style={{ ["--drag-y" as string]: `${dragDelta}px` } as React.CSSProperties}
          >
            {/* Group 1: title + actions */}
            <div className="bg-paper-elevated rounded-2xl overflow-hidden shadow-modal mb-2">
              <Dialog.Title
                data-testid="action-sheet-title"
                className="px-4 pt-3 pb-2 text-center text-body-sm text-ink-3 truncate"
              >
                {title}
              </Dialog.Title>
              <ul className="divide-y divide-hairline" data-testid="action-sheet-actions">
                {sorted.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => {
                        a.onClick();
                        onClose();
                      }}
                      disabled={a.disabled}
                      data-testid={a.testId ?? `action-sheet-action-${a.id}`}
                      data-tone={a.tone ?? "default"}
                      data-action-id={a.id}
                      className={cn(
                        "press flex w-full items-center justify-center gap-2 py-3.5 text-body font-medium",
                        "hover:bg-black/5 active:bg-black/10 transition-colors duration-quick ease-paper",
                        a.tone === "danger" ? "text-partner-strong" : "text-ink-1",
                        "disabled:opacity-50 disabled:cursor-not-allowed",
                      )}
                    >
                      {a.leadingIcon ? (
                        <span aria-hidden className="shrink-0">
                          {a.leadingIcon}
                        </span>
                      ) : null}
                      <span>{a.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Group 2: cancel (separate visual cluster per iOS HIG) */}
            {cancelLabel !== null ? (
              <div className="bg-paper-elevated rounded-2xl overflow-hidden shadow-modal">
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="action-sheet-cancel"
                  data-tone="cancel"
                  className={cn(
                    "press flex w-full items-center justify-center py-3.5 text-body font-semibold",
                    "text-you-strong hover:bg-black/5 active:bg-black/10 transition-colors duration-quick ease-paper",
                  )}
                >
                  {cancelLabel}
                </button>
              </div>
            ) : null}
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
